import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateAligners } from '../../../../../services/dmAligners';
import { setMonitoringInterval } from '../../../../../services/dmMonitoring';
import { setNextScanDate } from '../../../../../services/dmMonitoringControl';
import {
    initialDraft,
    nextScanChanged,
    touchedSettings,
    type DmBaseline,
    type DmDraft
} from './dmControlDraft';
import { errorText } from './dmControlUtils';
import { dmControlMutationKey } from './useDmControlMutation';
import { useFlash } from './useFlash';

type Step = () => Promise<unknown>;

// The next scan goes last, so the interval change doesn't move it again.
function saveSteps(patientId: string, base: DmBaseline, draft: DmDraft): Step[] {
    const steps: Step[] = [];
    const touched = touchedSettings(base, draft);
    if (touched.schedule) {
        const noGo = Number(draft.noGo);
        steps.push(() =>
            setMonitoringInterval(patientId, {
                intervalDays: Number(draft.interval),
                ...(base.needsNoGo ? { requestControlScanAfterDays: noGo } : {})
            })
        );
    }
    if (touched.aligner) {
        const initial = initialDraft(base);
        const current = Number(draft.current);
        const total = Number(draft.total);
        steps.push(() =>
            updateAligners(patientId, {
                ...(current !== Number(initial.current) ? { current } : {}),
                ...(total !== Number(initial.total) ? { total } : {})
            })
        );
    }
    if (nextScanChanged(base, draft)) {
        steps.push(() => setNextScanDate(patientId, { dueDate: draft.nextScan }));
    }
    return steps;
}

interface SaveInput {
    base: DmBaseline;
    draft: DmDraft;
}

// One save sends each changed setting to DM in turn and stops at the first refusal; what went
// through stays, and the refetch shows it.
export function useDmControlSave(patientId: string, onSaved: () => void) {
    const queryClient = useQueryClient();
    const [saved, setSaved] = useFlash<true>();
    const refresh = () => queryClient.invalidateQueries({ queryKey: ['v2/patients', patientId] });
    const mutation = useMutation({
        mutationKey: dmControlMutationKey(patientId),
        // The refetch finishes before onSuccess, so the caller rebuilds its draft on fresh data.
        mutationFn: async ({ base, draft }: SaveInput) => {
            for (const step of saveSteps(patientId, base, draft)) await step();
            await refresh();
        },
        onMutate: () => setSaved(null),
        onError: () => void refresh(),
        onSuccess: () => {
            setSaved(true);
            onSaved();
        }
    });
    return {
        save: mutation.mutate,
        reset: mutation.reset,
        saving: mutation.isPending,
        saved: saved === true,
        error: mutation.isError ? errorText(mutation.error) : null
    };
}
